import AssessmentOutlined from '@mui/icons-material/AssessmentOutlined';
import BadgeOutlined from '@mui/icons-material/BadgeOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlined from '@mui/icons-material/EditOutlined';
import EventNoteOutlined from '@mui/icons-material/EventNoteOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HotelOutlined from '@mui/icons-material/HotelOutlined';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined';
import PhoneOutlined from '@mui/icons-material/PhoneOutlined';
import PersonOutline from '@mui/icons-material/PersonOutline';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import StickyNote2Outlined from '@mui/icons-material/StickyNote2Outlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import WorkOutline from '@mui/icons-material/WorkOutline';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AlertColor,
  Avatar,
  Box,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { useCallback, useState, type MouseEvent, type ReactNode } from 'react';
import { useSelector } from 'react-redux';
import CKEditor5 from 'components/CkEditor5';
import Notification from 'components/Notification';
import axiosInstance from 'utils/axios';
import { privileges } from 'utils/constants/privileges';
import { URLS } from 'utils/constants/urls';
import { Privilege } from 'utils/redux/reducer/authentication-slice';
import { downloadExportFile, formatGuestCountCompact } from 'utils/validators/HelperFunctions';
import Currency from 'utils/hooks/getCurrency';
import strings from '../../../../common/Translation/Translate';
import { getPricePackageName } from 'utils/pricePackage';
import {
  BillingBreakdownPaper,
  PaymentHistoryPaper,
  type LineItemBillingPaymentState,
} from './BookingBillingPaymentPanels';
import { BookingDetailSectionTitle } from './BookingDetailSectionTitle';
import { CustomerDetailsEdit } from './CustomerDetailsEdit';

dayjs.extend(advancedFormat);

function formatLineTime(start?: string, end?: string) {
  const parseT = (t?: string) => {
    if (!t) return '';
    const d = dayjs(t, ['HH:mm:ss', 'HH:mm', 'H:mm']);
    return d.isValid() ? d.format('HH:mm') : t;
  };
  if (!start && !end) return '—';
  if (start && end) return `${parseT(start)}\u2013${parseT(end)}`;
  return parseT(start ?? end);
}

function pick(line: any, key: string): string | number {
  const v = line?.[key];
  if (v === 0) return 0;
  if (v !== undefined && v !== null && v !== '') return v;
  return '—';
}

/** Label + value pairs in a responsive grid (2 columns on mobile, 3 on sm+). */
function InfoGridRow({
  cells,
  columns,
}: {
  cells: { label: string; value: ReactNode; infoIcon?: boolean; infoTooltip?: string }[];
  /** Keep column widths aligned with other 3-col rows when a cell is omitted. */
  columns?: number;
}) {
  const n = columns ?? cells.length;
  const xsSpan = n === 1 ? 12 : 6;
  const smSpan = n === 1 ? 12 : n === 2 ? 6 : 4;

  return (
    <Box sx={{ mb: 2 }}>
      <Grid container spacing={{ xs: 1.5, sm: 2 }}>
        {cells.map(c => (
          <Grid item xs={xsSpan} sm={smSpan} key={c.label}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, letterSpacing: 0.02, display: 'block', lineHeight: 1.2 }}>
                {c.label}
              </Typography>
              {c.infoIcon && c.infoTooltip && (
                <Tooltip title={c.infoTooltip} arrow>
                  <InfoOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />
                </Tooltip>
              )}
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word', mt: 0.25 }}>
              {c.value}
            </Typography>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

function PriceLine({ label, value, emphasized }: { label: string; value: string; emphasized?: boolean }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(6.25rem, max-content)',
        columnGap: 1,
        alignItems: 'baseline',
        py: 0.125,
      }}>
      <Typography
        variant="body2"
        color={emphasized ? 'text.primary' : 'text.secondary'}
        fontWeight={emphasized ? 600 : 400}
        sx={{ fontSize: '0.8125rem' }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={emphasized ? 700 : 600}
        color="text.primary"
        sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
        {value}
      </Typography>
    </Box>
  );
}

function RoomGrandTotalLine({ label, value }: { label: string; value: string }) {
  return (
    <Box
      display="grid"
      gridTemplateColumns="minmax(0, 1fr) minmax(6.25rem, max-content)"
      alignItems="baseline"
      columnGap={1}
      sx={{
        py: 1.125,
        px: 1.25,
        bgcolor: theme => alpha(theme.palette.primary.main, 0.05),
        borderRadius: 1,
      }}>
      <Typography variant="subtitle1" fontWeight={800} color="primary.main" sx={{ fontSize: '1rem', lineHeight: 1.3 }}>
        {label}
      </Typography>
      <Typography
        variant="subtitle1"
        fontWeight={800}
        color="primary.main"
        sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: '1.25rem', lineHeight: 1.25 }}>
        {value}
      </Typography>
    </Box>
  );
}

function computeRoomGrandTotal(booking: any): number | null {
  if (booking.totalRoomDetailsCost != null) {
    return Number(booking.totalRoomDetailsCost);
  }

  const parts: number[] = [];
  if (booking.totalRoomCost != null) parts.push(Number(booking.totalRoomCost));
  if (booking.totalRoomServiceCharge != null) parts.push(Number(booking.totalRoomServiceCharge));

  if (booking.roomTaxBreakup?.length) {
    booking.roomTaxBreakup.forEach((tax: { amount?: number }) => {
      if (tax.amount != null) parts.push(Number(tax.amount));
    });
  } else {
    if (booking.totalRoomTax != null) parts.push(Number(booking.totalRoomTax));
    if (booking.totalRoomTaxSurcharge != null) parts.push(Number(booking.totalRoomTaxSurcharge));
  }

  if (!parts.length) return null;
  return parts.reduce((sum, amount) => sum + (Number.isFinite(amount) ? amount : 0), 0);
}

const customerContactIconSx = { fontSize: 20, color: 'text.secondary', mt: 0.35, flexShrink: 0 } as const;

function initialsFromName(name: string) {
  const p = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (p.length === 0) return '?';
  if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
  return (p[0][0] + p[p.length - 1][0]).toUpperCase();
}

interface VenueCustomerDetailsProps {
  bookingLineItem: any;
  onSuccess: () => void;
  isDeleted: boolean;
  billingPayment: LineItemBillingPaymentState;
}

function BookingCreationNoteField({
  bookingId,
  value,
  canEdit,
  onSaved,
}: {
  bookingId?: number;
  value: string;
  canEdit: boolean;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({ display: false, severity: null, message: '' });

  const displayHtml = value?.trim() ? value : '';
  const showEmpty = !displayHtml;

  const startEdit = (e: MouseEvent) => {
    e.stopPropagation();
    setDraft(value || '');
    setEditing(true);
  };

  const cancelEdit = (e?: MouseEvent) => {
    e?.stopPropagation();
    setEditing(false);
    setDraft('');
  };

  const saveEdit = async (e?: MouseEvent) => {
    e?.stopPropagation();
    if (!bookingId) return;
    try {
      setSaving(true);
      await axiosInstance.patch(URLS.updateBookingAdditionalComments(bookingId), {
        additionalComments: draft,
      });
      setEditing(false);
      setMessage({
        display: true,
        severity: 'success',
        message: strings.bookingDetails_creation_note_updated,
      });
      onSaved();
    } catch (error: any) {
      setMessage({
        display: true,
        severity: 'error',
        message:
          error?.response?.data?.error_description ||
          error?.response?.data?.message ||
          error?.message ||
          strings.bookingDetails_creation_note_update_failed,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={() => setMessage(m => ({ ...m, display: false }))}
          autoHideDuration={4000}
        />
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          mb: 0.5,
          '& .creation-note-edit': {
            opacity: 0,
            transition: 'opacity 0.15s ease',
          },
          '&:hover .creation-note-edit': {
            opacity: 1,
          },
        }}>
        <Typography variant="body2" color="primary" sx={{ fontWeight: 700, display: 'block' }}>
          {strings.bookingDetails_text_booking_creation_comment}
        </Typography>
        {canEdit && !editing && (
          <IconButton
            className="creation-note-edit"
            size="small"
            aria-label="Edit booking creation note"
            onClick={startEdit}
            sx={{ p: 0.25 }}>
            <EditOutlined sx={{ fontSize: 16 }} />
          </IconButton>
        )}
        {canEdit && editing && (
          <Stack direction="row" spacing={0.25} onClick={e => e.stopPropagation()}>
            <IconButton
              size="small"
              aria-label="Save booking creation note"
              onClick={saveEdit}
              disabled={saving}
              color="success"
              sx={{ p: 0.25 }}>
              {saving ? <CircularProgress size={14} /> : <CheckIcon sx={{ fontSize: 18 }} />}
            </IconButton>
            <IconButton
              size="small"
              aria-label="Cancel editing booking creation note"
              onClick={cancelEdit}
              disabled={saving}
              color="error"
              sx={{ p: 0.25 }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>
        )}
      </Box>
      {editing ? (
        <Box onClick={e => e.stopPropagation()}>
          <CKEditor5 label="" textChange={(html: string) => setDraft(html)} value={draft} />
        </Box>
      ) : showEmpty ? (
        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
          —
        </Typography>
      ) : (
        <Typography
          variant="body2"
          component="div"
          sx={{ fontWeight: 500, wordBreak: 'break-word', lineHeight: 1.45 }}
          dangerouslySetInnerHTML={{ __html: displayHtml }}
        />
      )}
    </Box>
  );
}

const VenueCustomerDetails = ({ bookingLineItem, onSuccess, isDeleted, billingPayment }: VenueCustomerDetailsProps) => {
  const theme = useTheme();
  const [openCustomerDrawer, setOpenCustomerDrawer] = useState(false);
  const line = bookingLineItem;
  const booking = line?.booking ?? {};

  const userPrivileges = useSelector(Privilege);
  const canEditCreationNote = !!booking?.id && userPrivileges?.includes(privileges.editBooking) && !isDeleted;
  const canDownloadInvoice =
    !!line?.id &&
    !line?.deleted &&
    line?.hasActualization &&
    userPrivileges?.includes(privileges.download_actualization);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const handleDownloadInvoice = useCallback(async (format: string = 'PDF') => {
    if (!line?.id) return;
    const documentFormat = format === 'DOCX' ? 'DOCX' : 'PDF';
    try {
      setDownloadingInvoice(true);
      const response = await axiosInstance({
        url: URLS.downloadActualizationPDF(line.id, undefined, undefined, documentFormat),
        method: 'GET',
        responseType: 'blob',
      });
      downloadExportFile(response, documentFormat.toLowerCase(), 'Bill');
    } catch (err) {
      // Silently fail; the panel doesn't surface notifications.
    } finally {
      setDownloadingInvoice(false);
    }
  }, [line?.id]);

  const venueName = pick(line, 'venueName');
  const segment = booking?.eventTypeName ? booking.eventTypeName : '—';
  const eventSubtype = line?.eventSubtype ? line.eventSubtype : '—';
  const eventDate = line.startDate ? dayjs(line.startDate).format('D MMM YYYY') : '—';
  const bookingDate = booking?.bookingDate ? dayjs(booking.bookingDate).format('D MMM YYYY') : '—';
  const timeSlotDisplay =
    (line?.timeSlotName &&
      String(line.timeSlotName)
        .trim()
        .replace(/\s*-\s*/, '\u2013')) ||
    formatLineTime(line?.startTime, line?.endTime);
  const guestLine = formatGuestCountCompact(line?.guaranteedGuests, line?.expectedGuests);
  const pricePackageName = line?.hourlyPricing ? '—' : getPricePackageName(line, '—');
  const pricePerPersonDisplay = line?.hourlyPricing
    ? line?.formattedPricePerHour ?? (line?.pricePerHour != null ? Currency(line.pricePerHour) : '—')
    : line?.formattedPricePerPerson ?? (line?.pricePerPerson != null ? Currency(line.pricePerPerson) : '—');

  const customBookingField = (key: string) => {
    const v = booking?.[key];
    if (v === 0) return 0;
    if (v !== undefined && v !== null && v !== '') return v;
    return '—';
  };

  // Hold-days release date only for tentative bookings (from booking creation date).
  const isTentative = String(line?.status || '').toUpperCase() === 'TENTATIVE';
  const holdDaysValue = customBookingField('holdDays');
  const holdDaysReleaseDate =
    isTentative && bookingDate !== '—' && holdDaysValue !== '—' && Number(holdDaysValue) >= 0
      ? dayjs(booking.bookingDate).add(Number(holdDaysValue), 'day').format('D MMM YYYY')
      : null;

  const customLineItemField = (key: string) => {
    const v = line?.[key];
    if (v === 0) return 0;
    if (v !== undefined && v !== null && v !== '') return v;
    return '—';
  };

  const customerName = String(customBookingField('customerName'));
  const companyNameRaw = String(customBookingField('companyName'));
  const showCompanyLine = Boolean(booking?.isCompany) && companyNameRaw && companyNameRaw !== '—';
  const phone = booking?.phoneResponse?.value ?? '';
  const phoneHref = phone ? `tel:${String(phone).replace(/[^\d+]/g, '')}` : '';
  const email = booking?.email ?? '';
  const emailHref = email ? `mailto:${String(email).trim()}` : '';
  const gst = booking?.gst ?? '';
  const panNumber = booking?.panNumber ?? '';
  const cityStatePin = [customBookingField('city'), customBookingField('state'), customBookingField('pinCode')]
    .filter(v => v && v !== '—')
    .join(', ');
  const countryLine =
    customBookingField('country') && customBookingField('country') !== '—' ? String(customBookingField('country')) : '';
  const addressLine1 = String(customBookingField('address'));

  const bookedRooms = booking.bookedRooms || [];
  const roomGrandTotal = computeRoomGrandTotal(booking);
  const externalRoom = bookedRooms.find((r: any) => r?.externalProvider);
  const isExternalRooms = !!externalRoom;
  const externalProvider = externalRoom?.externalProvider;
  const roomsSectionLabel = isExternalRooms
    ? `${externalProvider} ${strings.bookingDetails_text_rooms}`
    : strings.bookingDetails_text_rooms;

  const summaryPaperSx = {
    p: 2.25,
    borderRadius: 2,
    width: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    flex: 1,
  };

  return (
    <Grid container spacing={2} alignItems="stretch" sx={{ pt: 1 }}>
      <Grid item xs={12} md={6} sx={{ display: 'flex', px: 0 }}>
        <Paper variant="outlined" sx={summaryPaperSx}>
          <BookingDetailSectionTitle icon={EventNoteOutlined}>
            {strings.bookingDetail_section_eventSummary}
          </BookingDetailSectionTitle>
          <Divider sx={{ mb: 1.5 }} />

          <InfoGridRow
            cells={[
              { label: strings.venueDetails_text_date, value: eventDate },
              { label: strings.venueDetails_bookingDate, value: bookingDate },
              { label: strings.venueDetails_text_timeSlots, value: timeSlotDisplay },
            ]}
          />

          <InfoGridRow
            cells={[
              { label: strings.venueDetails_text_name, value: venueName },
              { label: strings.venueDetails_text_segment, value: segment },
              { label: strings.venueDetails_text_eventSubType, value: eventSubtype },
            ]}
          />

          <InfoGridRow
            cells={[
              { label: strings.venueDetails_text_guests, value: guestLine },
              {
                label: line?.hourlyPricing ? strings.pricePerHour : strings.bookingDetails_text_pricePerPerson,
                value: pricePerPersonDisplay,
              },
              {
                label: strings.new_booking_package,
                value: pricePackageName,
              },
            ]}
          />

          {line?.placeOfEvent && (
            <Box sx={{ mb: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                {strings.customerDetailsPlaceOfEvent}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.25 }}>
                {line.placeOfEvent}
              </Typography>
              {line?.addressOfEvent ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                  {line.addressOfEvent}
                </Typography>
              ) : null}
            </Box>
          )}

          <InfoGridRow
            columns={3}
            cells={[
              {
                label: strings.bookingDetails_compact_confirmedBy,
                value: String(customBookingField('bookingConfirmationBy')),
              },
              { label: strings.bookingDetails_text_referred_by, value: String(customBookingField('referredBy')) },
              ...(!holdDaysReleaseDate
                ? [
                    {
                      label: strings.venueConfig_general_holdDays,
                      value: String(customBookingField('holdDays')),
                    },
                  ]
                : []),
            ]}
          />
          {holdDaysReleaseDate ? (
            <InfoGridRow
              cells={[
                {
                  label: strings.venueConfig_general_holdDays,
                  value: (
                    <Box
                      component="span"
                      sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
                      <Box component="span">{String(customBookingField('holdDays'))}</Box>
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.35,
                          color: 'warning.dark',
                          fontWeight: 600,
                        }}>
                        <WarningAmberOutlined sx={{ fontSize: 15, color: 'warning.main' }} />
                        {(strings.booking_holdDays_release_hint || 'Releases on {date}').replace(
                          '{date}',
                          holdDaysReleaseDate,
                        )}
                      </Box>
                    </Box>
                  ),
                },
              ]}
            />
          ) : null}
        </Paper>
      </Grid>

      <Grid item xs={12} md={6} sx={{ display: 'flex', px: 0 }}>
        <Paper variant="outlined" sx={summaryPaperSx}>
          <BookingDetailSectionTitle
            icon={PersonOutline}
            action={
              <CustomerDetailsEdit
                bookingId={booking.id}
                onSuccess={onSuccess}
                openDrawer={openCustomerDrawer}
                setOpenDrawer={setOpenCustomerDrawer}
                isCompany={Boolean(booking.isCompany)}
                customerName={customerName}
                isDeleted={isDeleted}
                actualizationFinalized={false}
                hideNameRow
              />
            }>
            {strings.bookingDetail_section_customerSummary}
          </BookingDetailSectionTitle>
          <Divider sx={{ mb: 2 }} />

          <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 2.5 }}>
            <Avatar
              sx={{
                width: 44,
                height: 44,
                bgcolor: '#1565C0',
                color: '#fff',
                fontSize: '0.9rem',
                fontWeight: 700,
              }}>
              {initialsFromName(customerName)}
            </Avatar>
            <Box sx={{ minWidth: 0, pt: 0.25 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25, fontSize: '1.15rem' }}>
                {customerName}
              </Typography>
              {showCompanyLine ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 500 }}>
                  {companyNameRaw}
                </Typography>
              ) : null}
            </Box>
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <PhoneOutlined sx={customerContactIconSx} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, letterSpacing: 0.02, display: 'block', mb: 0.35 }}>
                    {strings.customerDetails_title_phone}
                  </Typography>
                  {phoneHref ? (
                    <Link href={phoneHref} variant="body2" underline="hover" sx={{ fontWeight: 500 }}>
                      {phone}
                    </Link>
                  ) : (
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      —
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <EmailOutlined sx={customerContactIconSx} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, letterSpacing: 0.02, display: 'block', mb: 0.35 }}>
                    {strings.customerDetails_title_email}
                  </Typography>
                  {emailHref ? (
                    <Link
                      href={emailHref}
                      variant="body2"
                      underline="hover"
                      sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
                      {email}
                    </Link>
                  ) : (
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      —
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <BadgeOutlined sx={customerContactIconSx} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, letterSpacing: 0.02, display: 'block', mb: 0.35 }}>
                    {strings.customerDetailsGST}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {gst || '—'}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <BadgeOutlined sx={customerContactIconSx} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, letterSpacing: 0.02, display: 'block', mb: 0.35 }}>
                    {strings.customermgmt_text_panNumber}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {panNumber || '—'}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <WorkOutline sx={customerContactIconSx} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, letterSpacing: 0.02, display: 'block', mb: 0.35 }}>
                    {strings.customerDetailsDesignation}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {String(customBookingField('designation'))}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>

          <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mt: 'auto', pt: 2.5 }}>
            <LocationOnOutlined sx={customerContactIconSx} />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, letterSpacing: 0.02, display: 'block', mb: 0.35 }}>
                {strings.customerDetails_text_address}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.45 }}>
                {addressLine1 && addressLine1 !== '—' ? addressLine1 : '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.45 }}>
                {[cityStatePin, countryLine].filter(Boolean).join(', ') || '—'}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6} sx={{ display: 'flex', px: 0 }}>
        <Box sx={{ flex: 1, width: '100%', minWidth: 0 }}>
          <BillingBreakdownPaper
            billingSummarySectionTitle={billingPayment.billingSummarySectionTitle}
            billingTitleIcon={line?.hasActualization ? AssessmentOutlined : ReceiptLongOutlined}
            costingLoading={billingPayment.costingLoading}
            costingError={billingPayment.costingError}
            costingPage={billingPayment.costingPage}
            billingPerGuestInsight={billingPayment.billingPerGuestInsight}
            downloadInvoice={canDownloadInvoice ? () => handleDownloadInvoice('PDF') : undefined}
            downloadInvoiceDocx={canDownloadInvoice ? () => handleDownloadInvoice('DOCX') : undefined}
            downloadInvoiceLoading={downloadingInvoice}
          />
        </Box>
      </Grid>

      {String(line?.status || '').toUpperCase() === 'CONFIRMED' && (
        <Grid item xs={12} md={6} sx={{ display: 'flex', px: 0 }}>
          <Box sx={{ flex: 1, width: '100%', minWidth: 0 }}>
            <PaymentHistoryPaper
              bookingLineItem={line}
              onSuccess={onSuccess}
              paymentsLoading={billingPayment.paymentsLoading}
              lineItemPayments={billingPayment.lineItemPayments}
              settlementEntries={billingPayment.settlementEntries}
              bookingAmountDue={billingPayment.bookingAmountDue}
              paymentMethods={billingPayment.paymentMethods}
              paymentMethodsLoading={billingPayment.paymentMethodsLoading}
            />
          </Box>
        </Grid>
      )}

      {bookedRooms.length > 0 && (
        <Grid item xs={12} sx={{ px: 0, mt: 2 }}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <BookingDetailSectionTitle icon={HotelOutlined}>{roomsSectionLabel}</BookingDetailSectionTitle>
            <Divider sx={{ mb: 1.5 }} />
            <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>{strings.bookingDetails_text_room_type}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{strings.bookingDetails_text_room_startDate}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{strings.bookingDetails_text_room_endDate}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {strings.bookingDetails_text_room_noOfRoom}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {strings.room_column_price}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bookedRooms.map((row: any, idx: number) => (
                    <TableRow key={`${row.roomTypeName}-${row.bookingFrom}-${idx}`}>
                      <TableCell>{row.roomTypeName}</TableCell>
                      <TableCell>{dayjs(row.bookingFrom).format('Do MMMM YYYY')}</TableCell>
                      <TableCell>{dayjs(row.bookingTo).format('Do MMMM YYYY')}</TableCell>
                      <TableCell align="right">{row.numberOfRooms}</TableCell>
                      <TableCell align="right">{row.totalCost}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {!isExternalRooms && (
              <Box sx={{ mt: 2 }}>
                <PriceLine
                  label={strings.bookingDetails_text_totalRoomCost}
                  value={booking.totalRoomCost != null ? Currency(booking.totalRoomCost) : '—'}
                  emphasized
                />

                {((booking.roomTaxBreakup?.length ?? 0) > 0 ||
                  (booking.totalRoomServiceCharge != null && booking.totalRoomServiceCharge > 0) ||
                  (booking.totalRoomTax != null && booking.totalRoomTax > 0) ||
                  (booking.totalRoomTaxSurcharge != null && booking.totalRoomTaxSurcharge > 0)) && (
                  <>
                    <Divider
                      sx={{
                        my: 0.75,
                        borderStyle: 'dashed',
                        borderColor: alpha(theme.palette.info.main, 0.22),
                      }}
                    />
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{ mb: 0.5, display: 'block', color: 'text.primary' }}>
                      {strings.bookingDetails_text_taxesAndServiceCharges}
                    </Typography>
                    <Box
                      sx={{
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        bgcolor: alpha(theme.palette.info.main, 0.04),
                      }}>
                      {booking.totalRoomServiceCharge != null && booking.totalRoomServiceCharge > 0 && (
                        <PriceLine
                          label={strings.bookingDetails_billing_serviceCharge}
                          value={Currency(booking.totalRoomServiceCharge)}
                        />
                      )}
                      {(booking.roomTaxBreakup ?? []).map((tax: { text?: string; amount?: number }, idx: number) => (
                        <PriceLine
                          key={`room-tax-${tax.text ?? idx}`}
                          label={tax.text || 'Tax'}
                          value={tax.amount != null ? Currency(tax.amount) : '—'}
                        />
                      ))}
                      {!(booking.roomTaxBreakup?.length > 0) &&
                        booking.totalRoomTax != null &&
                        booking.totalRoomTax > 0 && (
                          <PriceLine
                            label={strings.bookingDetails_text_totalRoomTax}
                            value={Currency(booking.totalRoomTax)}
                          />
                        )}
                      {!(booking.roomTaxBreakup?.length > 0) &&
                        booking.totalRoomTaxSurcharge != null &&
                        booking.totalRoomTaxSurcharge > 0 && (
                          <PriceLine
                            label={strings.bookingDetails_text_totalRoomSurcharge}
                            value={Currency(booking.totalRoomTaxSurcharge)}
                          />
                        )}
                    </Box>
                  </>
                )}

                {roomGrandTotal != null && (
                  <>
                    <Divider
                      sx={{
                        my: 0.75,
                        borderStyle: 'dashed',
                        borderColor: alpha(theme.palette.primary.main, 0.25),
                      }}
                    />
                    <RoomGrandTotalLine
                      label={strings.bookingDetails_billing_totalAmountLine}
                      value={Currency(roomGrandTotal)}
                    />
                  </>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
      )}

      <Grid item xs={12} sx={{ px: 0, mt: 2 }}>
        <Accordion
          defaultExpanded={false}
          disableGutters
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px !important',
            overflow: 'hidden',
            '&:before': { display: 'none' },
          }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              px: 2.25,
              py: 0.5,
              minHeight: 48,
              '& .MuiAccordionSummary-content': { my: 1, alignItems: 'center' },
            }}>
            <BookingDetailSectionTitle icon={StickyNote2Outlined} sx={{ mb: 0 }}>
              {strings.bookingDetail_section_bookingNotes}
            </BookingDetailSectionTitle>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2.25, pt: 0, pb: 2.25 }}>
            <Divider sx={{ mb: 1.5 }} />

            <BookingCreationNoteField
              bookingId={booking?.id}
              value={typeof booking?.additionalComments === 'string' ? booking.additionalComments : ''}
              canEdit={canEditCreationNote}
              onSaved={onSuccess}
            />

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="primary" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                {strings.bookingDetails_text_additional_comment}
              </Typography>
              <Typography
                variant="body2"
                component="div"
                sx={{ fontWeight: 500, wordBreak: 'break-word', lineHeight: 1.45 }}
                dangerouslySetInnerHTML={{ __html: String(customLineItemField('additionalComments') || '') }}
              />
            </Box>

            <Box>
              <Typography variant="body2" color="primary" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                {strings.bookingDetailsCancelReason}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word', lineHeight: 1.45 }}>
                {String(customLineItemField('cancelReason'))}
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Grid>
    </Grid>
  );
};

export default VenueCustomerDetails;
